import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { ISingleSettingResponse } from "../models/Setting";

export default function useSingleSetting(settingKey: string, loading: boolean = false) {
  const { get, post, put, del } = useApiHelper();
  const queryClient = useQueryClient();
  const staleTime = 1000 * 60 * 60 * 1; //1 hour

  const singleSetting = useQuery({
    queryKey: ["settingKey", settingKey],
    queryFn: async () => {
      try {
        return await get<ISingleSettingResponse>(`/setting/${settingKey}`);
      } catch (error: any) {
        // A setting that is absent (or not customer-visible) 404s, which is a
        // normal outcome rather than a failure. Resolve it as an absent value so
        // React Query caches it and staleTime applies: an *error* result is never
        // cached, is always stale, and refetches on every remount — which turned
        // /setting/{key} into a request storm for permanently-missing keys.
        if (error?.message?.includes("HTTP Error: 404")) {
          return {
            success: true,
            message: "Setting not found",
            data: null
          } as unknown as ISingleSettingResponse;
        }

        throw error;
      }
    },
    enabled: !loading,
    staleTime: staleTime,
    // Keep the cached result (including "absent") alive across unmount gaps,
    // otherwise it is garbage collected after 5m and refetched from scratch.
    cacheTime: staleTime,
    refetchOnWindowFocus: false
  });

  return {
    singleSetting
  };
}
